/**
 * `<EditorToolbar>` — ONE toolbar for every rich-text surface.
 *
 * Renders a preset (`toolbar-presets.ts`) as a single non-wrapping row:
 * items that don't fit collapse, lowest priority first, into the trailing
 * `⋯` More menu (`ToolbarMoreMenu.tsx`), with the widest item — the
 * "Tt Normal text ▾" dropdown — shrinking to `Tt ▾` before anything hides.
 * Dividers sit on group boundaries between adjacent VISIBLE items only.
 *
 * It owns the popovers items open through `ToolbarRunContext`: the link
 * editor (shared by the toolbar button, the wiki `/link` slash command via
 * the `wiki:open-link-popover` window event, and click-on-a-link editing)
 * and the emoji picker. Both portal to `body`, so their position in this
 * tree does not matter — only that they are rendered at all.
 *
 * `<MarkdownEditorToolbar>` is the same component rendered OUTSIDE
 * `<MarkdownEditor>` (the wiki doc's full-width row, the wiki comment
 * composers' action row). An external toolbar is a SIBLING of the editor,
 * so nothing re-renders it on a selection change: it subscribes to the
 * editor and re-renders on a SIGNATURE of the preset's active states —
 * `transaction` fires per keystroke, and a bare force-render would re-
 * render on every character, whereas an unchanged signature makes React
 * bail out. The signature is DERIVED from the preset (every item's
 * `activeStates`), so adding a button can no longer ship a stale highlight.
 */

import type { Editor } from '@tiptap/react'
import type { TFunction } from 'i18next'
import { type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmojiPickerPopover } from '@/features/chat/components/EmojiPickerPopover'

import { LinkEditorPopover } from '../LinkEditorPopover'
import type { EditorSurface } from '../extensions'
import { TEXT_STYLE_OPTIONS, TextStyleDropdown } from './TextStyleDropdown'
import { Divider, ToolbarButton } from './ToolbarButton'
import { ToolbarMoreMenu } from './ToolbarMoreMenu'
import type { MoreMenuEntry } from './more-menu-entries'
import { TOOLBAR_ITEMS, type ToolbarItem, type ToolbarRunContext } from './toolbar-items'
import { TOOLBAR_PRESETS, type ToolbarPresetId } from './toolbar-presets'
import { useToolbarOverflow } from './use-toolbar-overflow'

/** `w-px mx-0.5` → 1 + 2 + 2. */
const DIVIDER_WIDTH = 5

/** Inner top radius per host box (see `ToolbarPreset.hostRadius`). */
const ROW_TOP_RADIUS: Record<'md' | 'lg', string> = {
  md: 'rounded-t-[5px]',
  lg: 'rounded-t-[7px]',
}

export interface EditorToolbarProps {
  editor: Editor | null
  preset: ToolbarPresetId
  surface: EditorSurface
  /** `'card'` (bordered, muted fill) / `'flush'` (free-floating row over the page). Ignored when `bare`. */
  chrome?: 'card' | 'flush'
  /** Surfaces whose schema has no table drop the table item. */
  enableTables?: boolean
  /** Present when the hosting surface can persist files → the Attach item renders. */
  attachFiles?: (files: File[]) => void
  /**
   * Render as a BARE cluster — no wrapper chrome, no `role="toolbar"` —
   * for a host that nests the formats inside its own toolbar row (the
   * wiki comment composers). The cluster still measures itself so it
   * can overflow into ⋯ instead of wrapping the host row.
   */
  bare?: boolean
  /** Extra ⋯ rows (the wiki insert catalog). Already localized. */
  extraMenuEntries?: readonly MoreMenuEntry[]
  t: TFunction<'common'>
}

interface LinkPopoverState {
  open: boolean
  anchorRect: DOMRect | null
  initialUrl: string | null
  initialText: string
  slashContext: { from: number; to: number } | null
}

const CLOSED_LINK: LinkPopoverState = {
  open: false,
  anchorRect: null,
  initialUrl: null,
  initialText: '',
  slashContext: null,
}

const selectionRect = (editor: Editor): DOMRect | null => {
  const { from, to } = editor.state.selection
  try {
    const start = editor.view.coordsAtPos(from)
    const end = editor.view.coordsAtPos(to)
    const top = Math.min(start.top, end.top)
    const bottom = Math.max(start.bottom, end.bottom)
    const left = Math.min(start.left, end.left)
    const right = Math.max(start.right, end.right)
    return new DOMRect(left, top, right - left, bottom - top)
  } catch {
    return null
  }
}

function Glyph({ item }: { item: ToolbarItem }) {
  switch (item.glyph) {
    case 'bold':
      return <span className="font-bold">B</span>
    case 'italic':
      return <span className="italic">I</span>
    case 'underline':
      return <span className="underline">U</span>
    case 'strike':
      return <span className="line-through">S</span>
    case 'inline-code':
      return <span className="font-mono text-[11px]">{'</>'}</span>
    case 'subscript':
      return (
        <span aria-hidden="true">
          X<sub className="text-[10px]">2</sub>
        </span>
      )
    case 'superscript':
      return (
        <span aria-hidden="true">
          X<sup className="text-[10px]">2</sup>
        </span>
      )
    case 'emoji':
      return <span aria-hidden="true">😊</span>
    default: {
      const Icon = item.icon
      return <Icon size={16} strokeWidth={1.75} className="h-4 w-4" />
    }
  }
}

export function EditorToolbar({
  editor,
  preset: presetId,
  surface,
  chrome = 'card',
  enableTables = true,
  attachFiles,
  bare = false,
  extraMenuEntries,
  t,
}: EditorToolbarProps) {
  const preset = TOOLBAR_PRESETS[presetId]

  // Popover state — declared BEFORE any early return so hooks run
  // unconditionally on every render.
  const [linkPopover, setLinkPopover] = useState<LinkPopoverState>(CLOSED_LINK)
  const [emoji, setEmoji] = useState<{ open: boolean; anchorRect: DOMRect | null }>({
    open: false,
    anchorRect: null,
  })
  const emojiTriggerRef = useRef<HTMLElement | null>(null)
  /** The ⋯ trigger — the anchor for popovers launched from a menu row. */
  const moreTriggerRef = useRef<HTMLButtonElement | null>(null)

  // Bridge for the wiki `/link` slash command and click-on-a-link editing.
  // Both dispatch a window-level CustomEvent (they can't reach this
  // component's state directly); we resolve the popover anchor from the
  // editor's selection, matching the toolbar-button path. Event name is a
  // stable public contract shared with `slash-commands.ts`.
  useEffect(() => {
    if (!editor) return
    const handler = (event: Event) => {
      const detail =
        event instanceof CustomEvent
          ? (event.detail as
              | {
                  source?: 'slash-menu' | 'link-click'
                  selectionFrom?: number
                  selectionTo?: number
                  selectedText?: string
                  existingHref?: string
                  markFrom?: number
                  markTo?: number
                  anchorRect?: { left: number; top: number; right: number; bottom: number }
                }
              | undefined)
          : undefined
      const fromSlash = detail?.source === 'slash-menu'
      const fromLinkClick = detail?.source === 'link-click'

      // Link-click: move the selection onto the clicked mark BEFORE opening
      // so the popover sees the whole link (Text prefill + replace range).
      if (
        fromLinkClick &&
        typeof detail?.markFrom === 'number' &&
        typeof detail?.markTo === 'number'
      ) {
        editor.chain().focus().setTextSelection({ from: detail.markFrom, to: detail.markTo }).run()
        const rectInit = detail.anchorRect ?? { left: 0, top: 0, right: 0, bottom: 0 }
        const anchorRect = new DOMRect(
          rectInit.left,
          rectInit.top,
          rectInit.right - rectInit.left,
          rectInit.bottom - rectInit.top,
        )
        const existingText = editor.state.doc.textBetween(detail.markFrom, detail.markTo, ' ')
        setLinkPopover({
          open: true,
          anchorRect,
          initialUrl: detail.existingHref ?? null,
          initialText: existingText,
          slashContext: { from: detail.markFrom, to: detail.markTo },
        })
        return
      }

      const { from, to } = editor.state.selection
      const previous = editor.getAttributes('link').href as string | undefined
      setLinkPopover({
        open: true,
        anchorRect: selectionRect(editor),
        initialUrl: previous ?? null,
        initialText: fromSlash ? (detail?.selectedText ?? '') : '',
        slashContext: fromSlash
          ? { from: detail?.selectionFrom ?? from, to: detail?.selectionTo ?? to }
          : null,
      })
    }
    window.addEventListener('wiki:open-link-popover', handler)
    return () => window.removeEventListener('wiki:open-link-popover', handler)
  }, [editor])

  // Row items for this render: drop `table` when the surface has none and
  // `attach` when the host can't persist files. Keyed on the PRESENCE of
  // the attach handler, not its identity — a host that re-creates the
  // callback per render must not churn the item set (a new set means a
  // full re-measure pass, i.e. the row flashing to all-visible).
  const hasAttach = attachFiles !== undefined
  const rowItems = useMemo(
    () =>
      preset.items.filter(({ id }) => {
        if (id === 'table' && !enableTables) return false
        if (id === 'attach' && !hasAttach) return false
        return true
      }),
    [preset, enableTables, hasAttach],
  )
  const moreOnlyIds = useMemo(
    () =>
      preset.moreOnly.filter((id) => {
        if (id === 'table' && !enableTables) return false
        if (id === 'attach' && !hasAttach) return false
        return true
      }),
    [preset, enableTables, hasAttach],
  )
  const alwaysShowMore = moreOnlyIds.length > 0 || (extraMenuEntries?.length ?? 0) > 0

  const overflowSpecs = useMemo(
    () =>
      rowItems.map(({ id, priority }) => ({
        id,
        group: TOOLBAR_ITEMS[id].group,
        priority,
        hasCompactStage: id === 'text-style',
      })),
    [rowItems],
  )
  const layout = useToolbarOverflow({
    items: overflowSpecs,
    alwaysShowMore,
    gap: preset.gap,
    dividerWidth: DIVIDER_WIDTH,
    moreWidthFallback: preset.size === 'sm' ? 28 : 32,
  })

  if (!editor) {
    if (bare) return null
    const skeletonClass =
      chrome === 'flush'
        ? 'px-0 py-1'
        : presetId === 'chat'
          ? `${ROW_TOP_RADIUS[preset.hostRadius]} border-b border-border bg-bg-elevated px-2 py-1`
          : `${ROW_TOP_RADIUS[preset.hostRadius]} border-b border-border bg-bg-muted px-2 py-1`
    return <div className={skeletonClass} aria-hidden="true" />
  }

  /* ---------------------------- link popover ---------------------------- */

  const openLinkPopover = (event: MouseEvent<HTMLButtonElement>) => {
    // Unified flow: the toolbar button opens the same author surface as
    // `/link` and the click-to-edit path (URL + Text fields, insert-at-range
    // on submit). If the caret is inside a link, widen to the whole mark
    // first so the popover sees all of it.
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').run()
    } else {
      editor.commands.focus()
    }
    const { from, to } = editor.state.selection
    const visibleText = from === to ? '' : editor.state.doc.textBetween(from, to, ' ', ' ')
    const previous = editor.getAttributes('link').href as string | undefined
    setLinkPopover({
      open: true,
      anchorRect: selectionRect(editor) ?? event.currentTarget.getBoundingClientRect(),
      initialUrl: previous ?? null,
      initialText: visibleText,
      slashContext: { from, to },
    })
  }
  const closeLinkPopover = () => {
    setLinkPopover((prev) => ({ ...prev, open: false }))
    editor.commands.focus()
  }
  const applyLink = ({ url, text }: { url: string; text?: string }) => {
    const slashContext = linkPopover.slashContext
    if (slashContext) {
      // Insert `<a href={url}>{text}</a>` at the captured range; blank
      // Text + empty range falls back to the URL as the visible label.
      const visibleText = (text ?? '').trim().length > 0 ? (text as string) : url
      editor
        .chain()
        .focus()
        .insertContentAt(slashContext, [
          { type: 'text', text: visibleText, marks: [{ type: 'link', attrs: { href: url } }] },
        ])
        .run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  }

  /* ---------------------------- emoji picker ---------------------------- */

  const openEmojiPicker = (event: MouseEvent<HTMLButtonElement>) => {
    emojiTriggerRef.current = event.currentTarget
    setEmoji((prev) =>
      prev.open
        ? { open: false, anchorRect: null }
        : { open: true, anchorRect: event.currentTarget.getBoundingClientRect() },
    )
  }

  const ctx: ToolbarRunContext = { surface, openLinkPopover, openEmojiPicker, attachFiles }

  /* ------------------------------ ⋯ entries ----------------------------- */

  const toEntry = (item: ToolbarItem): MoreMenuEntry => ({
    id: item.id,
    label: t(item.label.key as never),
    icon: item.icon,
    group: item.menuGroup,
    active: item.isActive?.(editor) ?? false,
    shortcut: item.shortcut,
    keywords: item.keywords,
    run: () => {
      // Menu rows have no trigger element of their own; anchor popovers to
      // the ⋯ trigger (the item's row button is unmounted while it is in
      // the menu, and a detached element measures as an all-zero rect).
      const fakeEvent = {
        currentTarget: moreTriggerRef.current ?? editor.view.dom,
      } as unknown as MouseEvent<HTMLButtonElement>
      if (item.id === 'emoji' || item.id === 'link') {
        // The menu commits on `mousedown`, and both popovers close on a
        // document `mousedown` outside themselves — opened synchronously,
        // the same event bubbles on to their fresh listener and shuts them
        // before paint ("clicking Emoji in ⋯ did nothing"). Open after the
        // dispatch has finished.
        window.setTimeout(() => item.run(editor, ctx, fakeEvent), 0)
        return
      }
      item.run(editor, ctx, fakeEvent)
    },
  })

  // A Tt▾ that isn't in the row expands into its options as individual
  // Text rows — one row per action, never two (Quote is its own item on
  // every preset, so it is never in this list).
  //
  // "Normal text" (the paragraph) is listed ONLY while a heading is active —
  // it is the way back, not a style to apply. In the dropdown it is a radio
  // showing the current state; as a menu row it was a permanently checked
  // entry that could never do anything (sign-off finding).
  const textStyleEntries = (item: ToolbarItem): MoreMenuEntry[] =>
    TEXT_STYLE_OPTIONS.filter(
      (option) => !(option.key === 'paragraph' && option.isActive(editor)),
    ).map((option) => ({
      id: `text-style-${option.key}`,
      label: t(`editor.toolbar.${option.labelKey}`),
      icon: option.icon,
      group: 'text',
      active: option.isActive(editor),
      shortcut: option.shortcut,
      keywords: item.keywords,
      run: () => option.run(editor),
    }))

  const menuEntries: MoreMenuEntry[] = []
  for (const id of [...layout.hiddenIds, ...moreOnlyIds]) {
    const item = TOOLBAR_ITEMS[id as ToolbarItem['id']]
    if (item.id === 'text-style') menuEntries.push(...textStyleEntries(item))
    else menuEntries.push(toEntry(item))
  }
  if (extraMenuEntries) menuEntries.push(...extraMenuEntries)

  /* -------------------------------- row --------------------------------- */

  const visibleSet = new Set(layout.visibleIds)
  const children: ReactNode[] = []
  let previousGroup: string | null = null
  for (const { id } of rowItems) {
    if (!visibleSet.has(id)) continue
    const item = TOOLBAR_ITEMS[id]
    if (previousGroup !== null && previousGroup !== item.group) {
      children.push(<Divider key={`divider-${id}`} />)
    }
    previousGroup = item.group
    if (item.id === 'text-style') {
      children.push(
        <span key={id} ref={layout.itemRef(id)} className="inline-flex shrink-0">
          <TextStyleDropdown
            editor={editor}
            t={t}
            compact={layout.compact}
            size={preset.size}
            labelRef={layout.compactLabelRef}
          />
        </span>,
      )
      continue
    }
    const active = item.isActive?.(editor) ?? false
    const isEmoji = item.id === 'emoji'
    children.push(
      <span key={id} ref={layout.itemRef(id)} className="inline-flex shrink-0">
        <ToolbarButton
          label={
            item.id === 'link' && active ? t('editor.toolbar.linkEdit') : t(item.label.key as never)
          }
          active={isEmoji ? emoji.open : active}
          onClick={(event) => item.run(editor, ctx, event)}
          tone={preset.tone}
          size={preset.size}
          omitPressed={item.id === 'link' || item.id === 'attach' || item.id === 'table'}
          ariaHasPopup={isEmoji ? 'dialog' : undefined}
          ariaExpanded={isEmoji ? emoji.open : undefined}
        >
          <Glyph item={item} />
        </ToolbarButton>
      </span>,
    )
  }
  if (layout.showMore) {
    children.push(
      <ToolbarMoreMenu
        key="more"
        editor={editor}
        entries={menuEntries}
        tone={preset.tone}
        size={preset.size}
        triggerRef={(node) => {
          moreTriggerRef.current = node
          layout.moreRef(node)
        }}
      />,
    )
  }

  const popovers = (
    <>
      <LinkEditorPopover
        open={linkPopover.open}
        anchorRect={linkPopover.anchorRect}
        initialUrl={linkPopover.initialUrl}
        initialText={linkPopover.initialText}
        showTextField={linkPopover.slashContext !== null}
        onSubmit={applyLink}
        onUnlink={linkPopover.initialUrl !== null ? removeLink : undefined}
        onClose={closeLinkPopover}
      />
      <EmojiPickerPopover
        open={emoji.open}
        anchorRect={emoji.anchorRect}
        onSelect={(picked) => {
          // Literal Unicode keeps the source plain text and round-trips.
          editor.chain().focus().insertContent(picked).run()
          setEmoji({ open: false, anchorRect: null })
        }}
        onClose={() => setEmoji({ open: false, anchorRect: null })}
        triggerRef={emojiTriggerRef}
        onTabOut={() => editor.commands.focus()}
      />
    </>
  )

  const gapClass = preset.gap === 6 ? 'gap-1.5' : preset.gap === 2 ? 'gap-0.5' : 'gap-1'

  if (bare) {
    return (
      <>
        {popovers}
        <div
          ref={layout.containerRef}
          className={`inline-flex min-w-0 flex-1 flex-nowrap items-center overflow-hidden ${gapClass}`}
        >
          {children}
        </div>
      </>
    )
  }

  // Flush: no border / fill; `-ms-2` pulls the first glyph flush with the
  // prose below. Chat: blends with the bordered composer (`bg-bg-elevated`)
  // so per-button hover reads as a real state. Card: bordered, muted fill.
  const rowClass =
    chrome === 'flush'
      ? `-ms-2 flex flex-nowrap items-center overflow-hidden py-2 ${gapClass}`
      : presetId === 'chat'
        ? // Same inner radius as the card row: a square child box paints OVER the
          // host's rounded border at the corners (children paint after the
          // parent's border), so even a same-colour fill cuts the curve.
          `flex flex-nowrap items-center overflow-hidden ${ROW_TOP_RADIUS[preset.hostRadius]} border-b border-border bg-bg-elevated px-2 py-1 ${gapClass}`
        : // The row's muted fill takes the host box's INNER radius (host
          // radius − 1px border) — a square fill pokes past the curve at both
          // top corners, and a wrong radius leaves a sliver at the tip.
          `flex flex-nowrap items-center overflow-hidden ${ROW_TOP_RADIUS[preset.hostRadius]} border-b border-border bg-bg-muted px-2 py-1 ${gapClass}`

  return (
    <>
      {popovers}
      <div
        ref={layout.containerRef}
        className={rowClass}
        role="toolbar"
        aria-label={t('editor.toolbar.ariaLabel')}
      >
        {children}
      </div>
    </>
  )
}

/* ------------------------------------------------------------------------ */
/* External wrapper                                                         */
/* ------------------------------------------------------------------------ */

export interface MarkdownEditorToolbarProps {
  editor: Editor | null
  preset: ToolbarPresetId
  chrome?: 'card' | 'flush'
  enableTables?: boolean
  attachFiles?: (files: File[]) => void
  bare?: boolean
  extraMenuEntries?: readonly MoreMenuEntry[]
}

/** Node / mark names whose active state the preset can render — the re-render signature. */
const activeStatesFor = (presetId: ToolbarPresetId): readonly string[] => {
  const preset = TOOLBAR_PRESETS[presetId]
  const names = new Set<string>()
  for (const { id } of preset.items) for (const n of TOOLBAR_ITEMS[id].activeStates) names.add(n)
  for (const id of preset.moreOnly) for (const n of TOOLBAR_ITEMS[id].activeStates) names.add(n)
  return [...names]
}

export function MarkdownEditorToolbar({
  editor,
  preset,
  chrome = 'flush',
  enableTables = true,
  attachFiles,
  bare = false,
  extraMenuEntries,
}: MarkdownEditorToolbarProps) {
  const { t } = useTranslation('common')
  const states = useMemo(() => activeStatesFor(preset), [preset])

  const [, setActiveSignature] = useState('')
  useEffect(() => {
    if (!editor) return
    const sync = () => {
      if (editor.isDestroyed) return
      const next = states.filter((name) => editor.isActive(name)).join(',')
      setActiveSignature((prev) => (prev === next ? prev : next))
    }
    sync()
    editor.on('selectionUpdate', sync)
    editor.on('transaction', sync)
    return () => {
      editor.off('selectionUpdate', sync)
      editor.off('transaction', sync)
    }
  }, [editor, states])

  return (
    <EditorToolbar
      editor={editor}
      preset={preset}
      surface={TOOLBAR_PRESETS[preset].surface}
      chrome={chrome}
      enableTables={enableTables}
      attachFiles={attachFiles}
      bare={bare}
      extraMenuEntries={extraMenuEntries}
      t={t}
    />
  )
}
